import React, { createContext, useContext, useEffect, useState } from "react"
import { User as SupabaseUser } from "@supabase/supabase-js"
import { supabase } from "@/lib/supabase"
import { getUserById, isNetworkFetchError, syncOAuthProfile } from "@/lib/db"
import type { User } from "@/types"

interface AuthContextType {
  firebaseUser: SupabaseUser | null // Keep name for backward compatibility
  user: User | null
  userData: User | null // Alias for user for backward compatibility
  loading: boolean
  isConfigured: boolean
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
  firebaseUser: null,
  user: null,
  userData: null,
  loading: true,
  isConfigured: true,
  refreshUser: async () => {},
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<SupabaseUser | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  const refreshUser = async () => {
    if (firebaseUser) {
      try {
        const userData = await getUserById(firebaseUser.id)
        setUser(userData)
      } catch (error) {
        if (!isNetworkFetchError(error)) {
          console.error("[AuthContext] Failed to refresh user profile:", error)
        }
      }
    }
  }

  // Load user profile outside of the auth callback to avoid deadlocks
  // (Supabase docs warn against calling Supabase functions inside onAuthStateChange)
  const loadUserProfile = (authUser: SupabaseUser, event: string) => {
    setTimeout(async () => {
      try {
        // Sync OAuth profile data on sign-in events
        if (event === "SIGNED_IN") {
          const provider = authUser.app_metadata?.provider
          if (provider && provider !== "email") {
            const metadata = authUser.user_metadata
            if (metadata) {
              await syncOAuthProfile(authUser.id, metadata as Record<string, string | undefined>)
            }
          }
        }

        const userData = await getUserById(authUser.id)
        setUser(userData)
      } catch (err) {
        if (!isNetworkFetchError(err)) {
          console.error("[AuthContext] Error loading user profile:", err)
        }
      }
    }, 0)
  }

  useEffect(() => {
    // Use onAuthStateChange as the single source of truth.
    // INITIAL_SESSION fires immediately with the restored session from storage,
    // so there's no need to also call getSession().
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setFirebaseUser(session?.user ?? null)

      if (session?.user) {
        if (event === "INITIAL_SESSION" || event === "SIGNED_IN" || event === "USER_UPDATED") {
          loadUserProfile(session.user, event)
        }
      } else {
        setUser(null)
      }

      // Mark loading as done after the initial session is processed
      if (event === "INITIAL_SESSION") {
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  return (
    <AuthContext.Provider value={{ firebaseUser, user, userData: user, loading, isConfigured: true, refreshUser }}>{children}</AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
